import {
  newVaultElement,
  withoutAzureKeyVault,
} from "@p0security/integrations-vaulted-credential-shared/components";
import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";

import { ConnectorHostingWithServiceAccount } from "./connector-hosting.ts";
import { INVALID_ITEM_ID, isItemId } from "./item-id.ts";
import { INVALID_SECRET_NAME, isSecretName } from "./secret-name.ts";

/** Two items, copied from LaunchDarkly's, so that one connector can serve
 * several Miro teams and be reinstalled without touching them.
 *
 * `connector` is one deployment of P0's Miro (AI) connector: where it runs
 * and, from that, the identity it runs as. `team` is one Miro team: which
 * connector reaches it and where the connector finds the connector user's
 * access token.
 */
export const MiroAiComponents = InstallSpec({
  connector: ItemComponent({
    description:
      "Runs P0's Miro (AI) connector in your AWS account or Google Cloud project",
    label: "Miro (AI) connector",
    maturity: "beta",
    schema: {
      // The variant that names the connector's identity after the connector,
      // so each team can grant that identity read on its secret.
      hosting: ConnectorHostingWithServiceAccount,
    },
    type: "string",
    // A connector's request holds only `hosting`, so the install framework
    // gives nothing away when it repeats the request beside a refused id.
    validator: async (id) => (isItemId(id) ? undefined : INVALID_ITEM_ID),
  }),
  team: ItemComponent({
    description:
      "A Miro team whose boards P0 can share with its members. Requests name it by its identifier here.",
    label: "Miro team",
    maturity: "beta",
    // No item validator, though a team's id has the connector's rule
    // (`isItemId`). The install framework answers a failing item validator
    // with the whole request, and logs it, and a team's request may hold the
    // access token pasted in place of its secret's name. So the team's
    // assembler has to check the id itself.
    //
    // Every field the form asks for is `step: "new"`: the team's read grant
    // names its connector's identity and its secret, so a new connector,
    // vault or secret takes a new item.
    //
    // The form asks for the fields in this order.
    schema: {
      // A `connector` item's id.
      connector: Element({
        description:
          "The Miro (AI) connector that reaches this team. It must run in the same cloud as the team's secret.",
        label: "Connector",
        step: "new",
        type: "dynamic",
      }),
      vault: withoutAzureKeyVault(newVaultElement({ owner: "Miro team" })),
      tokenSecretName: Element({
        description:
          "The name, not the ARN or the full resource name, of the secret that holds the connector user's Miro access token, in the secret manager above: from a private Miro app with the boards:read, boards:write and team:read scopes. Only your connector reads it, never P0.",
        label: "Access token secret",
        step: "new",
        type: "string",
        validator: async (_id, name) =>
          isSecretName(name) ? undefined : INVALID_SECRET_NAME,
      }),
      // P0 reads these from the connector each time it validates the team.
      // A second team item with the same ID is refused.
      teamId: Element({
        description:
          "The Miro team the access token belongs to, read when an admin last checked the team",
        label: "Miro team ID",
        type: "generated",
      }),
      // A count, stored as the string a generated field holds. Zero is a
      // warning on the item: nothing can be requested until the connector
      // user owns or co-owns a board.
      shareableBoards: Element({
        description:
          "How many of the team's boards the connector user owns or co-owns, and so P0 can share, read when an admin last checked the team",
        label: "Shareable boards",
        type: "generated",
      }),
    },
    type: "string",
  }),
});
